import { useCallback, useEffect, useRef, useState } from "react";
import { APIConnectionError, APIError } from "../../api/errors";
import * as ml from "../../api/ml";
import { POLL_FAILURES_BEFORE_WARNING, POLL_MS, describeJob, isActive } from "../../lib/training";
import { useDialogs } from "../../ui/dialogs";
import { creditsChanged } from "../../lib/credits";
import { playCue } from "../../lib/sound";

/** Polling interval; a test can shorten it through localStorage ("modelbench.pollMs"). */
function pollMs(): number {
  try { return Number(window.localStorage.getItem("modelbench.pollMs")) || POLL_MS; } catch { return POLL_MS; }
}

export interface JobState {
  active: boolean;
  submitting: boolean;
  stopping: boolean;
  /** The yellow line under the buttons (_train_status_label). */
  statusText: string;
  log: string;
  metrics: ml.TrainingMetrics | null;
}

/**
 * The training job of a project: submit, poll every five seconds, resume one that is already running, and stop.
 * Shared by the Train and Auto-Annotate pages (both embed the same job controls on the desktop).
 * `onDone` runs when a job reaches a terminal state, so the page can re-read its stats.
 */
export function useTrainingJob(projectId: number, onDone: () => void, completeText: string) {
  const dlg = useDialogs();
  const [s, setS] = useState<JobState>({ active: false, submitting: false, stopping: false, statusText: "", log: "", metrics: null });
  const patch = (p: Partial<JobState>) => setS((cur) => ({ ...cur, ...p }));
  const timer = useRef<number | undefined>(undefined);
  const failures = useRef(0);
  const alive = useRef(true);
  const done = useRef(onDone);
  done.current = onDone;
  const complete = useRef(completeText);
  complete.current = completeText;

  const stopPolling = () => { window.clearInterval(timer.current); timer.current = undefined; };

  const poll = useCallback(async () => {
    let job: ml.TrainingJob;
    try { job = await ml.trainingStatus(projectId); }
    catch (e) {
      // One dropped poll is normal while the backend is busy; a run of them is worth saying.
      if (e instanceof APIConnectionError) {
        failures.current++;
        if (failures.current >= POLL_FAILURES_BEFORE_WARNING) patch({ statusText: "Lost contact with the backend — still retrying…" });
      }
      return;
    }
    if (!alive.current) return;
    failures.current = 0;
    if (["completed", "failed", "cancelled"].includes(job.status)) {
      stopPolling();
      patch({
        active: false, stopping: false, log: job.log,
        statusText: job.status === "completed" ? complete.current : job.status === "cancelled"
          ? "Training stopped. A checkpoint from the last completed epoch was saved, if any — see log below."
          : "Training failed — see log below.",
      });
      ml.trainingMetrics(projectId).then((m) => alive.current && patch({ metrics: m })).catch(() => {});
      done.current();
      creditsChanged();                // what the run did not use comes back
      if (job.status === "completed") playCue("trained"); else if (job.status === "failed") playCue("problem");
    } else {
      patch({ statusText: describeJob(job), ...(job.log ? { log: job.log } : {}) });
      ml.trainingMetrics(projectId).then((m) => alive.current && patch({ metrics: m })).catch(() => {});
    }
  }, [projectId]);

  const startPolling = useCallback(() => { stopPolling(); timer.current = window.setInterval(poll, pollMs()); }, [poll]);

  /** Pick up a pending or running job so reopening the page shows live progress. Resolves true if one was found. */
  const resume = useCallback(async (): Promise<boolean> => {
    try {
      const job = await ml.trainingStatus(projectId);
      if (!isActive(job.status) || !alive.current) return false;
      patch({ active: true, stopping: !!job.cancel_requested, statusText: `Resumed: a training job is already ${job.status} for this project…`, ...(job.log ? { log: job.log } : {}) });
      startPolling();
      return true;
    } catch { return false; }   // no training job has ever been submitted for this project
  }, [projectId, startPolling]);

  useEffect(() => { alive.current = true; return () => { alive.current = false; stopPolling(); }; }, [projectId]);

  const start = useCallback(async (params: Partial<ml.TrainParams>, config: string) => {
    patch({ submitting: true, statusText: "Submitting training job…" });
    try {
      const r = await ml.startTraining(projectId, params);
      patch({ submitting: false, active: true, stopping: false, metrics: null, statusText: `Training job submitted (${config}). Status: ${r.status}. Polling for progress…`, log: `Training job started (${config})…\n` });
      startPolling();
    } catch (e) { patch({ submitting: false, statusText: `Error: ${(e as Error).message}` }); }
    finally { creditsChanged(); }      // a run sets its credits aside when it is queued
  }, [projectId, startPolling]);

  const stop = useCallback(async () => {
    const ok = await dlg.confirm({
      title: "Stop training", yes: "Stop training", no: "Keep training",
      text: "Stop the current training job? The model will finish its current epoch and save a checkpoint before stopping — progress so far isn't lost.",
    });
    if (!ok) return;
    patch({ stopping: true });
    try {
      await ml.cancelTraining(projectId);
      patch({ statusText: "Cancelling… finishing the current epoch, then stopping." });
    } catch (e) {
      patch({ stopping: false });
      await dlg.alert({ title: "Could not stop training", text: `${e instanceof APIError ? e.detail : (e as Error).message}` });
    }
  }, [dlg, projectId]);

  const appendLog = (line: string) => setS((cur) => ({ ...cur, log: `${cur.log}${cur.log && !cur.log.endsWith("\n") ? "\n" : ""}${line}\n` }));
  return { state: s, patch, resume, start, stop, appendLog };
}
